<template>
  <div class="bg-gray-700">
    <div
      v-if="showWindowBar"
      class="h-[13px] border-0 border-t border-solid border-gray-500 bg-gray-900"
    ></div>
    <div
      class="relative flex justify-center border-0 border-b border-solid border-gray-500 bg-gray-900"
    >
      <div v-if="isScrollable">
        <div
          v-if="!isOnStart"
          class="gradient-left pointer-events-none absolute bottom-0 left-0 top-0 w-[92px]"
        ></div>
        <t-button
          v-if="!isOnStart"
          class="!absolute left-[20px] top-[8px] z-[10]"
          @click="moveLeft"
        >
          <template #icon>
            <font-awesome-icon :icon="['fas', 'angle-left']" />
          </template>
        </t-button>
        <t-button
          v-if="!isOnEnd"
          class="!absolute right-[20px] top-[8px] z-[10]"
          @click="moveRight"
        >
          <template #icon>
            <font-awesome-icon :icon="['fas', 'angle-right']" />
          </template>
        </t-button>
        <div
          v-if="!isOnEnd"
          class="gradient-right pointer-events-none absolute bottom-0 right-0 top-0 w-[92px]"
        ></div>
      </div>
      <ul
        ref="scrollable"
        class="mx-2 mb-[-1px] mt-0 flex max-w-fit list-none overflow-hidden bg-gray-900 p-0"
      >
        <li
          v-for="(tab, index) in tabs"
          :key="index"
          :class="{
            'bg-accent-1': tab === activeTab,
          }"
          class="rounded-md pt-[3px]"
        >
          <button
            v-if="tab.props && tab.props['tab-title']"
            :ref="`tab-${index}`"
            :data-testid="tab.key"
            :class="{
              'border-x border-b-transparent !bg-gray-700 text-black dark:!text-white':
                tab === activeTab,
              'hover:!mt-[-3px] hover:border-x hover:border-t hover:border-gray-500 hover:!px-[23px] hover:pt-[14px] hover:text-gray-50':
                tab !== activeTab,
              small: small,
            }"
            class="m-0 box-border cursor-pointer whitespace-nowrap rounded-t-md border-b border-solid border-gray-500 bg-gray-900 px-6 pb-[13px] pt-[12px] font-inter text-xs font-medium leading-[1] text-gray-100 transition duration-300"
            @click="handleTabChange(tab, index)"
          >
            {{ tab.props["tab-title"] }}
          </button>
        </li>
      </ul>
    </div>

    <div class="content">
      <slot />
    </div>
  </div>
</template>
<script lang="ts" setup>
import TButton from "../TButton/TButton.vue";
import { ref, watch, onMounted, useSlots, nextTick } from "vue";

const props = defineProps({
  small: {
    type: Boolean,
    default: false,
  },
  showWindowBar: {
    type: Boolean,
    default: false,
  },
  defaultTabIndex: {
    type: Number,
    default: 0,
  },
  stepScroll: {
    type: Number,
    default: 40,
  },
});

const emit = defineEmits(["changed"]);

const tabs = ref<any[]>([]);
const activeTab = ref(null);
const isOnStart = ref(true);
const isOnEnd = ref(false);
const maxScroll = ref(0);
const minScroll = ref(0);
const isScrollable = ref(false);
const MENU_WIDTH = 305;

const { default: defaultSlot } = useSlots();

const scrollable = ref();

watch(
  () => activeTab.value,
  (activeTab) => {
    tabs.value.map((tab) => (tab.show = tab == activeTab));
  },
  { deep: true },
);

function handleTabChange(tab: any, index: number) {
  activeTab.value = tab;

  if (index === 0) {
    scrollable.value.scrollLeft = minScroll.value;
  }

  if (index + 1 === tabs.value.length) {
    scrollable.value.scrollLeft = maxScroll.value;
  }

  if (scrollable.value.scrollLeft < maxScroll.value) {
    isOnEnd.value = false;
  }

  if (scrollable.value.scrollLeft <= minScroll.value) {
    isOnStart.value = true;
  }

  if (scrollable.value.scrollLeft > minScroll.value) {
    isOnStart.value = false;
  }

  if (scrollable.value.scrollLeft >= maxScroll.value) {
    isOnEnd.value = true;
  }

  emit("changed");

  adjustScrollableView(index);
}

function adjustScrollableView(index: number) {
  const adjustedScrollWidth = index * 50;
  scrollable.value.scrollLeft = adjustedScrollWidth;
}

function moveLeft() {
  scrollable.value.scrollLeft -= props.stepScroll;

  if (scrollable.value.scrollLeft < maxScroll.value) {
    isOnEnd.value = false;
  }

  if (scrollable.value.scrollLeft <= minScroll.value) {
    isOnStart.value = true;
  }
}

function moveRight() {
  scrollable.value.scrollLeft += props.stepScroll;

  if (scrollable.value.scrollLeft > minScroll.value) {
    isOnStart.value = false;
  }

  if (scrollable.value.scrollLeft >= maxScroll.value) {
    isOnEnd.value = true;
  }
}

function checkForScrollableSpace() {
  nextTick(() => {
    maxScroll.value = scrollable.value.scrollWidth - MENU_WIDTH;

    if (maxScroll.value > 0) {
      isScrollable.value = true;
    }
  });
}

onMounted(() => {
  if (defaultSlot === undefined) return;

  tabs.value = defaultSlot();

  activeTab.value = tabs.value[props.defaultTabIndex];
  checkForScrollableSpace();
});

defineExpose({
  activeTab,
  tabs,
  handleTabChange,
});
</script>
<style lang="scss">
.gradient-right {
  background: linear-gradient(270deg, #ededed 35.33%, rgba(237, 237, 237, 0) 100%);
}

.gradient-left {
  background: linear-gradient(90deg, #ededed 35.33%, rgba(237, 237, 237, 0) 100%);
}

.dark-theme {
  .gradient-left {
    background: linear-gradient(90deg, #13171e 35.33%, rgba(19, 23, 30, 0) 100%);
  }
  .gradient-right {
    background: linear-gradient(270deg, #13171e 35.33%, rgba(19, 23, 30, 0) 100%);
  }
}
</style>
